import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { IServerResponse } from '@/common/common-models'
import type { USER_ROLES } from '@/config/config-app-models'

export type AuthPermissions =
  | 'view:competitions'
  | 'create:competition'
  | 'edit:competition'
  | 'delete:competition'
  | 'view:admin-dashboard'
  | 'manage:users'
  | 'view:entries'
  | 'edit:entries'

export interface AuthVerifyResponse {
  user: AuthUser
  permissions: AuthPermissions[]
}

// Types
export interface AuthUser {
  id: number
  name: string
  email: string
  roles: USER_ROLES[]
  permissions?: AuthPermissions[]
}

/*

{
    "user": {
        "id": "103470485079985274928",
        "email": "nick@entry4sports.co.uk",
        "roles": [
            "admin"
        ]
    },
    "permissions": [
        "view:competitions",
        "create:competition",
        "edit:competition",
        "delete:competition",
        "view:admin-dashboard",
        "manage:users",
        "view:entries",
        "edit:entries"
    ]
}
 */

export interface AuthState {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

/**
 * Safely read a value from localStorage.
 * Returns null if storage is unavailable (insecure context, disabled cookies,
 * sandboxed iframe, browser privacy mode, etc.) instead of throwing.
 *
 * Fixes Sentry issues:
 *   - LITECLIENT-3: SecurityError: The operation is insecure.
 *   - LITECLIENT-N: SecurityError: Failed to read the 'localStorage' property
 *                   from 'Window': Access is denied for this document.
 *
 * Previously the store crashed at module-load time because
 * `localStorage.getItem(...)` was called unguarded, which propagated up
 * through Pinia store creation in main.ts and broke app boot.
 */
function safeGetItem(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null
    const storage = window.localStorage
    if (!storage) return null
    return storage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Safely write a value to localStorage. Silently no-ops on failure.
 */
function safeSetItem(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return
    const storage = window.localStorage
    if (!storage) return
    storage.setItem(key, value)
  } catch {
    // ignore — storage unavailable or quota exceeded
  }
}

/**
 * Safely remove a value from localStorage. Silently no-ops on failure.
 */
function safeRemoveItem(key: string): void {
  try {
    if (typeof window === 'undefined') return
    const storage = window.localStorage
    if (!storage) return
    storage.removeItem(key)
  } catch {
    // ignore — storage unavailable
  }
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<AuthUser | null>(null)
  const token = ref<string | null>(safeGetItem('auth_token'))
  const isLoading = ref<boolean>(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const userRoles = computed(() => {
    return user.value?.roles || []
  })
  // Backward compatibility: return first role or null
  const userRole = computed(() => {
    const roles = userRoles.value
    return roles.length > 0 ? roles[0] : null
  })
  const hasPermission = computed(() => (permission: string) => {
    return user.value?.permissions?.includes(permission as AuthPermissions) || false
  })

  // Actions
  const setToken = (newToken: string | null): void => {
    token.value = newToken
    if (newToken) {
      safeSetItem('auth_token', newToken)
    } else {
      safeRemoveItem('auth_token')
    }
  }

  const setUser = (newUser: AuthUser | null): void => {
    user.value = newUser
  }

  const setError = (newError: string | null): void => {
    error.value = newError
  }

  const setLoading = (loading: boolean): void => {
    isLoading.value = loading
  }

  const login = async (redirectUrl?: string): Promise<void> => {
    const API_BASE_URL = import.meta.env.VITE_APP_API_HOST || 'http://localhost:3000'

    // Debug: Log the API base URL being used
    console.log('🔐 Auth Debug - API_BASE_URL:', API_BASE_URL)
    console.log('🔐 Auth Debug - Environment:', import.meta.env.MODE)

    // Construct the success callback URL that will handle the token (path only, no origin)
    const basePath = window.location.pathname
    const cacheBuster = window.location.search
    const successUrl = `${basePath}${cacheBuster}#/auth-success`

    console.log('🔐 Auth Debug - Success URL:', successUrl)

    // Add redirect parameter if provided
    const params = new URLSearchParams()
    params.append('redirect_url', successUrl)
    if (redirectUrl) {
      params.append('redirect', redirectUrl)
    }

    window.location.href = `${API_BASE_URL}/api/auth/google?${params.toString()}`
  }

  const verifyToken = async (): Promise<boolean> => {
    if (!token.value) {
      return false
    }

    setLoading(true)
    setError(null)

    try {
      const API_BASE_URL = import.meta.env.VITE_APP_API_HOST || 'http://localhost:3000'
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.value}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      const data: AuthVerifyResponse = await response.json()

      /*
      {
    "user": {
        "id": "103470485079985274928",
        "email": "nick@entry4sports.co.uk",
        "iat": 1751873882,
        "exp": 1752478682
    }
}
       */

      if (data.user) {
        if (data.permissions) {
          data.user.permissions = data.permissions
        }

        setUser(data.user)
        setError(null)
        return true
      } else {
        throw new Error('Authentication failed: No user data received')
      }
    } catch (err) {
      console.error('Token verification failed:', err)
      setError('Authentication failed. Please try again.')
      logout()
      return false
    } finally {
      setLoading(false)
    }
  }

  const logout = (): void => {
    setUser(null)
    setToken(null)
    setError(null)
  }

  const handleTokenFromUrl = (): void => {
    // This method is now primarily handled by the AuthSuccess component
    // but we keep it for backward compatibility or direct token handling
    const urlParams = new URLSearchParams(window.location.search)
    const tokenFromUrl = urlParams.get('token')

    if (tokenFromUrl) {
      setToken(tokenFromUrl)
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname)
      // Verify the token
      verifyToken()
    }
  }

  const initialize = async (): Promise<void> => {
    // Check for token in URL first (OAuth redirect)
    handleTokenFromUrl()

    // If we have a token in storage, verify it
    if (token.value && !user.value) {
      await verifyToken()
    }
  }

  return {
    // State
    user,
    token,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    userRoles,
    userRole, // Backward compatibility
    hasPermission,
    // Actions
    setToken,
    setUser,
    setError,
    setLoading,
    login,
    verifyToken,
    logout,
    handleTokenFromUrl,
    initialize
  }
})
